import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

export type Direction = 'left' | 'right' | 'top' | 'bottom';

const ModalOverlay = styled(motion.div) <{ $show: boolean; $direction: Direction; $noOverlay?: boolean }>`
  width: 100vw;
  height: 100dvh;
  background-color: ${({ $noOverlay }) => ($noOverlay ? 'transparent' : 'rgba(0, 0, 0, 0.6)')};
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
  display: flex;
  justify-content: ${({ $direction }) =>
    $direction === 'right' ? 'flex-end' :
      $direction === 'left' ? 'flex-start' :
        'center'};
  align-items: ${({ $direction }) =>
    $direction === 'bottom' ? 'flex-end' :
      $direction === 'top' ? 'flex-start' :
        'center'};
  z-index: 3; // 인벤토리 아이템 모달이 가려지지 않는 수치
  backdrop-filter: ${({ $noOverlay }) => ($noOverlay ? 'none' : 'blur(8px)')};
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  pointer-events: ${({ $show, $noOverlay }) => {
    if (!$show) return 'none';
    return $noOverlay ? 'none' : 'auto';
  }};
  transition: opacity 0.3s ease;
`;

const getTransform = ($show: boolean, $direction: Direction) => {
  if ($show) return 'translate(0, 0)';

  switch ($direction) {
    case 'right':
      return 'translateX(100%)';
    case 'left':
      return 'translateX(-100%)';
    case 'top':
      return 'translateY(-100%)';
    case 'bottom':
      return 'translateY(100%)';
    default:
      return 'translateX(100%)';
  }
};

const getSizeStyles = ($direction: Direction, $maxWidth?: number) => {
  const isHorizontal = $direction === 'left' || $direction === 'right';

  if (isHorizontal) {
    return css`
      width: 80%;
      max-width: ${$maxWidth ? `${$maxWidth}px` : '360px'};
      height: 100dvh;
      height: calc(100dvh - calc(100dvh - 100%));
    `;
  } else {
    return css`
      width: 100vw;
      height: 80%;
      max-height: 600px;
    `;
  }
};

const getBorderStyles = ($direction: Direction) => {
  switch ($direction) {
    case 'right':
      return 'border-left: 1px solid var(--lineColor);';
    case 'left':
      return 'border-right: 1px solid var(--lineColor);';
    case 'top':
      return 'border-bottom: 1px solid var(--lineColor);';
    case 'bottom':
      return 'border-top: 1px solid var(--lineColor);';
    default:
      return '';
  }
};

const ModalContent = styled(motion.div) <{ $show: boolean; $direction: Direction; $maxWidth?: number; $noOverlay?: boolean }>`
  ${({ $direction, $maxWidth }) => getSizeStyles($direction, $maxWidth)};
  
  text-align: left;
  background: var(--backgroundColor);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  backdrop-filter: blur(8px);
  transform: ${({ $show, $direction }) => getTransform($show, $direction)};
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  will-change: transform, opacity;
  ${({ $direction }) => getBorderStyles($direction)};
  ${({ $noOverlay }) => $noOverlay && css`
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  `};

  @media (max-width: 460px) {
    width: ${({ $direction }) =>
    $direction === 'left' || $direction === 'right' ? '85%' : '100vw'};
    max-width: ${({ $direction, $maxWidth }) =>
    $direction === 'left' || $direction === 'right' ? ($maxWidth ? `${$maxWidth}px` : '360px') : 'none'};
  }
`;

type SideModalWrapperProps = {
  /** 모달 표시 여부 */
  show: boolean;
  /** 슬라이드 방향 (기본값: 'right') */
  direction?: Direction;
  /** 고유 키 */
  key?: string | number;
  /** 외부 클릭 시 실행될 함수 */
  outsideClick: () => void;
  /** 모달 내용 */
  children: JSX.Element | JSX.Element[];
  /** z-index 값 (기본값: 10) */
  zIndex?: number;
  /** 최대 너비 (기본값: 360px, 수평 방향에만 적용) */
  maxWidth?: number;
  /** 오버레이 비활성화 (기본값: false) - true일 경우 어두운 배경 없이 슬라이드만 표시 */
  noOverlay?: boolean;
};

function SideModalWrapper({
  show,
  direction = 'right',
  key = 'custom',
  outsideClick,
  children,
  zIndex = 10,
  maxWidth,
  noOverlay = false,
}: SideModalWrapperProps) {
  function handleModalOutsideClick() {
    if (outsideClick) {
      outsideClick();
    }
  }
  useEffect(() => {
    // 오버레이 모드에서만 바깥 스크롤 막기
    if (show && !noOverlay) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [show, noOverlay]);

  return (
    <ModalOverlay
      key={key}
      $show={show}
      $direction={direction}
      $noOverlay={noOverlay}
      onClick={noOverlay ? undefined : handleModalOutsideClick}
      style={{ zIndex }}
    >
      <ModalContent
        $show={show}
        $direction={direction}
        $maxWidth={maxWidth}
        $noOverlay={noOverlay}
        onClick={(e) => e.stopPropagation()}
        aria-hidden={!show}
        {...(!show ? { inert: '' as any } : {})}
      >
        {children}
      </ModalContent>
    </ModalOverlay>
  );
}

export default SideModalWrapper;
